import React, { useEffect, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { prefetchSpeech } from "../../services/cloudVoice";
import { motion } from "framer-motion";
import { launchConfetti } from "../../utils/confetti";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { buildReturnPath } from "../../utils/buildReturnPath";
import "./TalkChoice.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const backgroundMusicUrl =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fmultiple-choice.mp3?alt=media&token=0fb4b3ca-5778-4d14-b866-95cf37855d2d";

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";

// What is read for one question: the question, then each choice. Built in
// one place so the warm-up (prefetchSpeech) and the reading use the exact
// same sentence — a different comma would be a different, unmade sentence.
const lineFor = (q) => {
  if (!q) return "";
  const answerText = (q.answers || [])
    .map((answer, index) => {
      const label = answer.alt || answer.text || `Answer ${index + 1}`;
      return `Choice ${index + 1}: ${label}`;
    })
    .join(". ");
  return `${q.speak || q.question}. ${answerText}`;
};

const TalkChoice = ({ questions = [], onComplete, user, topic }) => {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame(); // back to the subject page this game came from
  const { subject } = useParams();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [details, setDetails] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const musicRef = useRef(null);
  const correctRef = useRef(null);
  const incorrectRef = useRef(null);

  const current = questions[currentIndex];
  const passingScore = Math.ceil(questions.length * 0.8);

  // Get every question's voice ready as soon as the game opens, so each one
  // is read the moment it appears instead of being made while the child waits.
  useEffect(() => {
    prefetchSpeech([...questions.map(lineFor), "Nice job!", "Good try."]);
  }, [questions]);

  useEffect(() => {
    musicRef.current = new Audio(backgroundMusicUrl);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.25;
    musicRef.current.play().catch(() => {});

    correctRef.current = new Audio(
      "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306"
    );

    incorrectRef.current = new Audio(
      "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686"
    );

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
      }

      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const stopSpeaking = () => {
    if (!window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    setIsSpeaking(false);

    if (musicRef.current && !isMuted) {
      musicRef.current.volume = 0.25;
    }
  };

  const speak = (text, kind = "question") => {
    if (!voiceOn || !window.speechSynthesis || !text) return;

    stopSpeaking();

    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(String(text));

    utter.rate = 1.12; // a bit quicker — kids found the questions slow
    utter.sblKind = kind; // speed from the admin's read-aloud settings
    utter.pitch = 1;
    utter.volume = 1;
    utter.lang = "en-US";

    const voices = synth.getVoices();
    const preferred =
      voices.find((v) => /en-US/i.test(v.lang) && /Google|Samantha|Jenny|Aria/i.test(v.name)) ||
      voices.find((v) => /en-US/i.test(v.lang)) ||
      voices[0];

    if (preferred) {
      utter.voice = preferred;
    }

    if (musicRef.current) {
      musicRef.current.volume = 0.08;
    }

    setIsSpeaking(true);

    utter.onend = () => {
      setIsSpeaking(false);
      if (musicRef.current && !isMuted) {
        musicRef.current.volume = 0.25;
      }
    };

    utter.onerror = () => {
      setIsSpeaking(false);
      if (musicRef.current && !isMuted) {
        musicRef.current.volume = 0.25;
      }
    };

    synth.speak(utter);
  };

  const speakQuestionAndAnswers = () => {
    if (!current) return;

    speak(lineFor(current));
  };

  useEffect(() => {
    if (!current) return;

    const timer = setTimeout(() => {
      speakQuestionAndAnswers();
    }, 250);

    return () => clearTimeout(timer);
  }, [currentIndex, voiceOn]);

  const handleReturnHome = () => leaveGame();

  const handleAnswerSelect = (answer) => {
    if (selectedAnswer || !current) return;

    stopSpeaking();
    setSelectedAnswer(answer);

    const isCorrect = !!answer.correct;
    const correctAnswer = (current.answers || []).find((a) => a.correct);

    const detail = {
      qIndex: currentIndex,
      question: current.question,
      selectedText: answer.text || answer.alt || (answer.image ? "[image]" : ""),
      correctText:
        correctAnswer?.text ||
        correctAnswer?.alt ||
        (correctAnswer?.image ? "[image]" : ""),
      correct: isCorrect,
    };

    const nextDetails = [...details];
    nextDetails[currentIndex] = detail;
    setDetails(nextDetails);

    if (isCorrect) {
      playGameSound("correct", "talkChoice", () => {
        if (correctRef.current) {
          correctRef.current.currentTime = 0;
          correctRef.current.play().catch(() => {});
        }
      });

      speak("Nice job!", "answer");
    } else {
      playGameSound("wrong", "talkChoice", () => {
        if (incorrectRef.current) {
          incorrectRef.current.currentTime = 0;
          incorrectRef.current.play().catch(() => {});
        }
      });

      speak("Good try.", "answer");
    }

    const nextScore = score + (isCorrect ? 1 : 0);

    setTimeout(() => {
      const nextIndex = currentIndex + 1;

      if (nextIndex < questions.length) {
        setScore(nextScore);
        setCurrentIndex(nextIndex);
        setSelectedAnswer(null);
      } else {
        if (nextScore >= passingScore) {
          launchConfetti();
        }

        onComplete?.({
          score: nextScore,
          answers: nextDetails,
          details: nextDetails,
        });
      }
    }, 1000);
  };

  if (!current) {
    return (
      <div className="talk-choice-wrapper">
        <div className="talk-choice-container">
          <h2>No Talk Choice questions found.</h2>
          <button className="mini-btn home" onClick={handleReturnHome}>
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="talk-choice-wrapper qk-scene"
    >
      <div className="hud">
        <div className="score-card">
          <div className="row">
            <span>Score:</span>
            <strong>{score}</strong>
          </div>
          <div className="row">
            <span>Question:</span>
            <strong>
              {currentIndex + 1}/{questions.length}
            </strong>
          </div>
        </div>

        <div className="controls">
          <button
            className="mini-btn"
            onClick={() => setIsMuted((prev) => !prev)}
          >
            {isMuted ? "🔈 Music" : "🔇 Music"}
          </button>

          <button
            className="mini-btn"
            onClick={() => setVoiceOn((prev) => !prev)}
          >
            {voiceOn ? "🗣️ Voice On" : "🤫 Voice Off"}
          </button>

          <button className="mini-btn" onClick={speakQuestionAndAnswers}>
            🔊 Speak
          </button>

          {isSpeaking && (
            <button className="mini-btn" onClick={stopSpeaking}>
              ⏹ Stop
            </button>
          )}

          <button className="mini-btn home" onClick={handleReturnHome}>
            🏠 Home
          </button>
        </div>
      </div>

      <div className="talk-choice-container">
        <img src={LOGO_URL} alt="SBL Logo" className="brand-logo" />

        <h2 className="quiz-question">{current.question}</h2>

        <div className="answers-grid">
          {(current.answers || []).map((answer, i) => {
            const isSelected = selectedAnswer === answer;

            const className = `choice-tile ${
              isSelected ? (answer.correct ? "correct" : "incorrect") : ""
            }`;

            const label =
              answer.alt || answer.text || (answer.image ? "image answer" : "answer");

            return (
              <motion.button
                key={i}
                type="button"
                onClick={() => handleAnswerSelect(answer)}
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.04 }}
                className={className}
                aria-label={label}
              >
                {answer.image && (
                  <img
                    src={answer.image}
                    alt={label}
                    className="answer-image"
                    draggable="false"
                  />
                )}

                {answer.text && <span className="answer-text">{answer.text}</span>}
              </motion.button>
            );
          })}
        </div>

        <div className="footer">
          © Smart Board Learning LLC. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default TalkChoice;